/**
 * 텍스트 기반 커버 이미지 자동 생성
 * - EPUB 커버 이미지가 없을 때 메타데이터로 커버 생성
 * - 타사(Icecream Ebook Reader) 스타일 참고
 */

// 그라데이션 색상 팔레트 (랜덤 선택)
const GRADIENT_PALETTES = [
  { start: '#1a5c4c', mid: '#2d8b70', end: '#3eb489' },  // 초록 (타사 스타일)
  { start: '#1e3a5f', mid: '#2e5a8f', end: '#3e7abf' },  // 파랑
  { start: '#5c1a5c', mid: '#8b2d8b', end: '#b43eb4' },  // 보라
  { start: '#5c3a1a', mid: '#8b5a2d', end: '#b47a3e' },  // 갈색
  { start: '#1a1a5c', mid: '#2d2d8b', end: '#3e3eb4' },  // 남색
  { start: '#5c1a3a', mid: '#8b2d5a', end: '#b43e7a' },  // 분홍
  { start: '#2d5a3a', mid: '#4a8b5a', end: '#6bb47a' },  // 연두
  { start: '#4a4a5c', mid: '#6a6a8b', end: '#8a8ab4' },  // 회색-보라
];

/**
 * Canvas로 커버 이미지 생성
 */
export async function generateCoverImage(
  title: string,
  author: string,
  width: number = 300,
  height: number = 400
): Promise<Blob> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;

    // 랜덤 그라데이션 색상 선택
    const palette = GRADIENT_PALETTES[Math.floor(Math.random() * GRADIENT_PALETTES.length)];

    // 그라데이션 배경
    const gradient = ctx.createLinearGradient(0, 0, width * 0.3, height);
    gradient.addColorStop(0, palette.start);
    gradient.addColorStop(0.5, palette.mid);
    gradient.addColorStop(1, palette.end);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // 미세한 패턴 오버레이 (고급스러움)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.03)';
    for (let i = 0; i < height; i += 4) {
      ctx.fillRect(0, i, width, 1);
    }

    // 상단 어두운 오버레이
    const topOverlay = ctx.createLinearGradient(0, 0, 0, height * 0.4);
    topOverlay.addColorStop(0, 'rgba(0, 0, 0, 0.2)');
    topOverlay.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = topOverlay;
    ctx.fillRect(0, 0, width, height * 0.4);

    // 텍스트 설정
    ctx.textAlign = 'center';
    ctx.fillStyle = '#ffffff';

    // 제목 (여러 줄 처리)
    const titleLines = wrapText(ctx, title, width - 50, 22);
    ctx.font = 'bold 22px "Nanum Gothic", "Apple SD Gothic Neo", sans-serif';
    
    const titleStartY = height * 0.28;
    titleLines.forEach((line, index) => {
      // 텍스트 그림자
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.fillText(line, width / 2 + 1, titleStartY + (index * 30) + 1);
      // 실제 텍스트
      ctx.fillStyle = '#ffffff';
      ctx.fillText(line, width / 2, titleStartY + (index * 30));
    });

    // 구분선
    const lineY = titleStartY + (titleLines.length * 30) + 25;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(width * 0.25, lineY);
    ctx.lineTo(width * 0.75, lineY);
    ctx.stroke();

    // 저자
    ctx.font = '16px "Nanum Gothic", "Apple SD Gothic Neo", sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillText(author, width / 2, lineY + 35);

    // 하단 WIA Book 로고
    ctx.font = '11px "Nanum Gothic", "Apple SD Gothic Neo", sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.fillText('WIA Book', width / 2, height - 18);

    // Canvas → Blob
    canvas.toBlob((blob) => {
      resolve(blob!);
    }, 'image/jpeg', 0.92);
  });
}

/**
 * 텍스트 줄바꿈 처리
 */
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, fontSize: number): string[] {
  ctx.font = `bold ${fontSize}px "Nanum Gothic", "Apple SD Gothic Neo", sans-serif`;
  
  const lines: string[] = [];
  let currentLine = '';

  // 한글 포함 여부 확인
  const hasKorean = /[\u3131-\uD79D]/.test(text);

  if (hasKorean) {
    // 한글은 글자 단위로 줄바꿈
    const chars = text.split('');
    for (const char of chars) {
      const testLine = currentLine + char;
      const metrics = ctx.measureText(testLine);
      
      if (metrics.width > maxWidth && currentLine.length > 0) {
        lines.push(currentLine);
        currentLine = char;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) {
      lines.push(currentLine);
    }
  } else {
    // 영어는 단어 단위로 줄바꿈
    const words = text.split(' ');
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      
      if (metrics.width > maxWidth && currentLine.length > 0) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) {
      lines.push(currentLine);
    }
  }

  // 최대 3줄까지만
  return lines.slice(0, 3);
}

/**
 * 메타데이터에서 커버 생성
 */
export async function generateCoverFromMetadata(
  metadata: { title?: string; creator?: string }
): Promise<Blob> {
  const title = metadata.title || '제목 없음';
  const author = metadata.creator || '작자 미상';
  
  return generateCoverImage(title, author);
}
